"use client";

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { HexColorPicker, HexColorInput } from "react-colorful";
import { Pipette } from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import { cn } from "../lib/cn";
import { normalizeHex } from "../lib/colorUtils";
import { useReducedMotion } from "../hooks/useReducedMotion";
import { useEyeDropper } from "../hooks/useEyeDropper";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "../ui/popover";
import { springs, transitions } from "../lib/motion";

/**
 * ColorPicker — one accessible HSV color picker for the whole platform.
 *
 * Consolidates three hand-rolled donor pickers (`unified:src/shell/MoodLighting`,
 * `fe-react:.../ColorPickerPopover`, plus a third inline copy) that each
 * reimplemented a global `mousemove`/`mouseup` drag loop with no touch, no
 * keyboard, and no a11y. The saturation square + hue strip are now
 * [react-colorful](https://github.com/omgovich/react-colorful)'s
 * `<HexColorPicker>` (pointer + touch + keyboard, ~2.8kB, zero deps), re-skinned
 * to the brand via its documented class hooks; the hex field is its
 * `<HexColorInput>` (sanitize + clamp built in, replacing the manual regex).
 *
 * On top of react-colorful this adds the AISquare flavor: a branded hex field
 * with a leading `#` affordance, an optional eyedropper button (native
 * EyeDropper API via {@link useEyeDropper}, omitted where unsupported), and an
 * optional preset-swatch row. The companion {@link ColorPickerPopover}
 * (`ColorPicker.Popover`) folds the whole thing into the brand `<Popover>` with
 * a spring-in, matching fe-react's controlled + popover shape.
 *
 * Color math lives in `colorUtils` (`hexToRgb` / `rgbToHex` / `hexToHSV` /
 * `hsvToHex`), exported from the same package; the picker itself keeps its
 * state as the hex string react-colorful manages and only reaches for
 * `colorUtils` to compare a value against the presets (`normalizeHex`).
 *
 * Light theme only. All chrome resolves to brand tokens (`--card` / `--popover`
 * surface, `--border` hairline, `--muted` / `--accent` for the eyedropper,
 * `--ring` for focus) — no `bg-white` / `gray-*` / raw hex. Every animation is
 * gated on `useReducedMotion()` with a static fallback.
 *
 * @example Uncontrolled, with presets and an eyedropper:
 *   <ColorPicker defaultValue="#daa520" onChange={setColor} presets={COLOR_PICKER_PRESETS} />
 *
 * @example Controlled, inside a trigger-anchored popover:
 *   <ColorPicker.Popover value={color} onChange={setColor}>
 *     <button>Pick color</button>
 *   </ColorPicker.Popover>
 */

/** The donor palette (unified `MoodLighting` presets) — a sensible default row. */
export const COLOR_PICKER_PRESETS = [
  "#daa520",
  "#0a2540",
  "#10B981",
  "#3B82F6",
  "#8B5CF6",
  "#EF4444",
  "#EC4899",
] as const;

/** Fallback color when neither `value` nor `defaultValue` is supplied. */
const FALLBACK_COLOR = "#daa520";

const colorPickerVariants = cva("flex flex-col gap-3", {
  variants: {
    size: {
      sm: "[--cp-square:140px]",
      md: "[--cp-square:180px]",
    },
  },
  defaultVariants: {
    size: "md",
  },
});

export interface ColorPickerProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onChange" | "defaultValue" | "color">,
    VariantProps<typeof colorPickerVariants> {
  /** Controlled hex value (`#rrggbb`). Pair with `onChange`. */
  value?: string;
  /** Initial hex value when uncontrolled. Defaults to the brand gold. */
  defaultValue?: string;
  /** Fires with the next `#rrggbb` hex on every change. */
  onChange?: (hex: string) => void;
  /**
   * Preset swatches shown in a row under the picker. Omit to hide the row.
   * Defaults to {@link COLOR_PICKER_PRESETS}; pass `[]` to explicitly hide it.
   */
  presets?: readonly string[];
  /**
   * Show the eyedropper button. Defaults to `undefined` = auto (shown only when
   * the browser supports the EyeDropper API). Pass `false` to force it off.
   */
  showEyedropper?: boolean;
}

/**
 * Resolve controlled-vs-uncontrolled hex state. When `value` is provided the
 * component is controlled; otherwise it owns the state seeded from
 * `defaultValue`.
 */
function useColorState(
  value: string | undefined,
  defaultValue: string | undefined,
  onChange: ((hex: string) => void) | undefined,
): [string, (hex: string) => void] {
  const isControlled = value !== undefined;
  const [internal, setInternal] = React.useState(defaultValue ?? FALLBACK_COLOR);
  const current = isControlled ? value : internal;

  const set = React.useCallback(
    (hex: string) => {
      if (!isControlled) setInternal(hex);
      onChange?.(hex);
    },
    [isControlled, onChange],
  );

  return [current, set];
}

const ColorPickerRoot = React.forwardRef<HTMLDivElement, ColorPickerProps>(
  (
    {
      className,
      size,
      value,
      defaultValue,
      onChange,
      presets = COLOR_PICKER_PRESETS,
      showEyedropper,
      ...props
    },
    ref,
  ) => {
    const reduced = useReducedMotion();
    const [color, setColor] = useColorState(value, defaultValue, onChange);
    const { supported: eyedropperSupported, open: openEyedropper } = useEyeDropper();

    // Auto when `showEyedropper` is undefined; an explicit `false` always wins.
    // `supported` is `null` until the client resolves, so the button stays
    // hidden during SSR / first paint and never flashes away on hydration.
    const renderEyedropper = showEyedropper !== false && eyedropperSupported === true;

    const normalizedColor = normalizeHex(color);

    const handleEyedropper = React.useCallback(async () => {
      const picked = await openEyedropper();
      if (picked) setColor(picked);
    }, [openEyedropper, setColor]);

    return (
      <div
        ref={ref}
        data-slot="color-picker"
        className={cn(colorPickerVariants({ size }), className)}
        {...props}
      >
        <ColorPickerSkin />

        <HexColorPicker color={color} onChange={setColor} />

        <div className="flex items-center gap-2">
          <div
            className={cn(
              "flex flex-1 items-center rounded-md border border-input bg-background",
              "px-2.5 text-sm",
              "focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2 focus-within:ring-offset-background",
            )}
          >
            <span aria-hidden className="select-none font-mono text-muted-foreground">
              #
            </span>
            <HexColorInput
              color={color}
              onChange={setColor}
              prefixed={false}
              aria-label="Hex color"
              className="h-9 w-full bg-transparent font-mono uppercase text-foreground outline-none"
            />
          </div>

          {renderEyedropper ? (
            <button
              type="button"
              onClick={handleEyedropper}
              aria-label="Pick a color from the screen"
              className={cn(
                "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md",
                "bg-muted text-muted-foreground transition-colors duration-180 ease-smooth-out",
                "hover:bg-accent/10 hover:text-accent-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
              )}
            >
              <Pipette className="size-4" aria-hidden />
            </button>
          ) : null}
        </div>

        {presets.length > 0 ? (
          <div className="flex flex-wrap gap-2" role="group" aria-label="Color presets">
            {presets.map((preset) => {
              const isActive = normalizeHex(preset) === normalizedColor;
              return (
                <PresetSwatch
                  key={preset}
                  color={preset}
                  active={isActive}
                  reduced={reduced}
                  onSelect={() => setColor(preset)}
                />
              );
            })}
          </div>
        ) : null}
      </div>
    );
  },
);
ColorPickerRoot.displayName = "ColorPicker";

/**
 * One preset swatch. Hover-scales (Tailwind) and shows a spring-in ring when it
 * is the active color; both micro-interactions collapse under reduced motion.
 */
function PresetSwatch({
  color,
  active,
  reduced,
  onSelect,
}: {
  color: string;
  active: boolean;
  reduced: boolean | null;
  onSelect: () => void;
}) {
  const allowMotion = reduced !== true;
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-label={`Use color ${color}`}
      aria-pressed={active}
      style={{ backgroundColor: color }}
      className={cn(
        "relative size-7 rounded-full border border-border/40 transition-transform duration-180 ease-smooth-out",
        allowMotion && "hover:scale-110",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      )}
    >
      <AnimatePresence>
        {active ? (
          <motion.span
            aria-hidden
            className="pointer-events-none absolute -inset-1 rounded-full ring-2 ring-ring"
            initial={allowMotion ? { scale: 0.6, opacity: 0 } : false}
            animate={{ scale: 1, opacity: 1 }}
            exit={allowMotion ? { scale: 0.6, opacity: 0 } : { opacity: 0 }}
            transition={allowMotion ? springs.gentle : { duration: 0 }}
          />
        ) : null}
      </AnimatePresence>
    </button>
  );
}

export interface ColorPickerPopoverProps
  extends Pick<ColorPickerProps, "presets" | "showEyedropper" | "size"> {
  /** Controlled hex value (`#rrggbb`). */
  value: string;
  /** Fires with the next `#rrggbb` hex on every change. */
  onChange: (hex: string) => void;
  /** The popover trigger — wrapped with Radix `asChild`, so pass one element. */
  children: React.ReactNode;
  /** Forwarded to Radix `PopoverContent`. Default `"start"`. */
  align?: "start" | "center" | "end";
  /** Forwarded to Radix `PopoverContent`. Default `"bottom"`. */
  side?: "top" | "right" | "bottom" | "left";
  /** Extra classes on the popover content panel. */
  className?: string;
}

/**
 * ColorPicker.Popover — the controlled picker folded into the brand `<Popover>`.
 *
 * Matches fe-react's `ColorPickerPopover`: fully controlled (`value` /
 * `onChange`), `children` is the trigger via `PopoverTrigger asChild`, and the
 * panel springs in (scale `0.96 → 1` + fade) reusing the lib's surface motion
 * preset, with an opacity-only / instant fallback under reduced motion. Radix
 * keeps focus trap, dismiss, and positioning.
 */
export const ColorPickerPopover = React.forwardRef<HTMLDivElement, ColorPickerPopoverProps>(
  ({ value, onChange, children, align = "start", side = "bottom", className, ...rest }, ref) => {
    const reduced = useReducedMotion();
    const allowMotion = reduced !== true;

    return (
      <Popover>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          ref={ref}
          align={align}
          side={side}
          data-slot="color-picker-popover"
          className={cn("w-auto p-0", className)}
        >
          <motion.div
            initial={allowMotion ? { scale: 0.96, opacity: 0 } : false}
            animate={{ scale: 1, opacity: 1 }}
            transition={allowMotion ? springs.gentle : transitions.surface}
            className="p-4"
          >
            <ColorPickerRoot value={value} onChange={onChange} {...rest} />
          </motion.div>
        </PopoverContent>
      </Popover>
    );
  },
);
ColorPickerPopover.displayName = "ColorPicker.Popover";

/**
 * The public `<ColorPicker>` — the root picker with `.Popover` attached as a
 * static sub-component, so consumers can write either `<ColorPicker />` or
 * `<ColorPicker.Popover>` (matching fe-react's call shape). Typed as the
 * intersection so the `.Popover` member is visible to consumers and TS.
 */
type ColorPickerComponent = typeof ColorPickerRoot & {
  Popover: typeof ColorPickerPopover;
};

export const ColorPicker = ColorPickerRoot as ColorPickerComponent;
ColorPicker.Popover = ColorPickerPopover;

/**
 * Re-skins react-colorful to the brand via its documented class hooks, scoped
 * to `[data-slot="color-picker"]` so it never bleeds onto another instance.
 * react-colorful injects its own base CSS at runtime (client-side, after
 * mount), so emitting this static `<style>` is SSR-safe, and the attribute
 * selector out-specifies its single-class defaults. Dimensions read the
 * `--cp-square` var set by the `size` variant.
 */
const SKIN_CSS = `
[data-slot="color-picker"] .react-colorful {
  width: 100%;
  height: auto;
  gap: 12px;
  display: flex;
  flex-direction: column;
}
[data-slot="color-picker"] .react-colorful__saturation {
  height: var(--cp-square, 180px);
  border-radius: var(--radius);
  border: 1px solid hsl(var(--border));
}
[data-slot="color-picker"] .react-colorful__hue {
  height: 12px;
  border-radius: 9999px;
}
[data-slot="color-picker"] .react-colorful__saturation-pointer,
[data-slot="color-picker"] .react-colorful__hue-pointer {
  width: 18px;
  height: 18px;
  border: 2px solid #fff;
  box-shadow: 0 0 0 1px hsl(var(--border)), 0 1px 3px hsl(0 0% 0% / 0.3);
}
[data-slot="color-picker"] .react-colorful__interactive:focus-visible .react-colorful__pointer {
  outline: 2px solid hsl(var(--ring));
  outline-offset: 2px;
}
`;

function ColorPickerSkin() {
  return <style>{SKIN_CSS}</style>;
}
